import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { TICKETS_REFRESH_EVENT } from "../../utils/ticketRefresh";
import { groupInternalTickets } from "../../utils/internalConversations";
import { useNotificationSound } from "../../context/NotificationSound";
import { isTicketMuted } from "../../utils/ticketMute";
import formatContactDisplayName from "../../utils/formatContactDisplayName";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
	notificationIconWrapper: {
		position: "relative",
		display: "flex",
		alignItems: "center",
	},
	notificationBadges: {
		position: "absolute",
		top: "50%",
		right: -20,
		transform: "translateY(-50%)",
		display: "flex",
		flexDirection: "column",
		gap: 3,
	},
	notificationCounter: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minWidth: 18,
		height: 18,
		padding: "0 4px",
		borderRadius: 9,
		color: "#fff",
		fontSize: 11,
		fontWeight: 600,
		lineHeight: 1,
		boxSizing: "border-box",
	},
	openCounter: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},
	pendingCounter: {
		backgroundColor: theme.palette.secondary.main,
	},
}));

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const { whatsApps } = useContext(WhatsAppsContext);
	const { playNotificationSound } = useNotificationSound();
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [indicatorsRefreshKey, setIndicatorsRefreshKey] = useState(0);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({
		withUnreadMessages: "true",
		refreshKey: indicatorsRefreshKey,
	});
	const userQueueIds = user?.queues?.map(queue => queue.id) || [];
	const { tickets: rawPendingTickets } = useTickets({
		status: "pending",
		queueIds: JSON.stringify(userQueueIds),
		refreshKey: indicatorsRefreshKey,
	});
	const { tickets: openTickets } = useTickets({
		status: "open",
		showAll: user?.profile?.toUpperCase() === "ADMIN" ? "true" : undefined,
		queueIds: JSON.stringify(userQueueIds),
		refreshKey: indicatorsRefreshKey,
	});
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	useEffect(() => {
		soundAlertRef.current = playNotificationSound;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else {
			Notification.requestPermission();
		}
	}, [playNotificationSound]);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();
		const refreshIndicators = () => {
			setIndicatorsRefreshKey(previous => previous + 1);
		};

		socket.on("connect", () => socket.emit("joinNotification"));

		socket.on("ticket", data => {
			refreshIndicators();

			if (
				data.action === "updateUnread" ||
				data.action === "delete" ||
				(data.action === "update" && data.ticket?.status === "closed")
			) {
				setNotifications(prevState => {
					const updatedTicketId = data.ticketId || data.ticket?.id;
					const ticketIndex = prevState.findIndex(t => t.id === updatedTicketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		});

		socket.on("appMessage", data => {
			refreshIndicators();

			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup ||
					isTicketMuted(data.ticket);

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});
		window.addEventListener(TICKETS_REFRESH_EVENT, refreshIndicators);

		return () => {
			window.removeEventListener(TICKETS_REFRESH_EVENT, refreshIndicators);
			socket.disconnect();
		};
	}, [user]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${formatContactDisplayName(contact)}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const pendingTickets = groupInternalTickets(rawPendingTickets, whatsApps).length;
	const unreadOpenMessages = groupInternalTickets(openTickets, whatsApps)
		.reduce((total, ticket) => total + (ticket.unreadMessages || 0), 0);

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<div className={classes.notificationIconWrapper}>
					<ChatIcon />
					<div
						className={classes.notificationBadges}
						title={`${unreadOpenMessages} mensajes nuevos · ${pendingTickets} tickets en cola`}
					>
						{unreadOpenMessages > 0 && (
							<span
								className={`${classes.notificationCounter} ${classes.openCounter}`}
							>
								{unreadOpenMessages > 99 ? "99+" : unreadOpenMessages}
							</span>
						)}
						{pendingTickets > 0 && (
							<span
								className={`${classes.notificationCounter} ${classes.pendingCounter}`}
							>
								{pendingTickets > 99 ? "99+" : pendingTickets}
							</span>
						)}
					</div>
				</div>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
